/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */

/** import package [start] */
import { FC, useState, useEffect, createRef } from 'react';
import { Breadcrumb } from '@zeeve-platform/breadcrumb-component';
import { Modal } from '@zeeve-platform/modal-component';
/** import package [end] */

/** import custom dependency [start] */
import iconShardeum from '../../../assets/shardeum_dark.svg';
import FullSizeLoader from '../../../components/FullSizeLoader';
import BaseUrls from '../../../utils/baseUrls';
import { deleteTheEndpoint } from '../../../services/endpoint';
import basicImage from '../../../assets/user.svg';
import metricsImage from '../../../assets/SoundCloud.png';
import Basic, { IFRef as BIFRef } from './Basic';
import Metrics from './Metrics';
/** import custom dependency [end] */

type Tab = {
    element: JSX.Element;
    name: string;
    icon?: JSX.Element;
};

/** type declaration for End Point details props */
export interface EndpointDetailsProps {
    id?: string;
}

/**
 * End-Point Details component
 * @returns JSX.Element
 */
const EndpointDetails: FC<EndpointDetailsProps> = ({ id }) => {
    // misc states
    const [isLoading, setLoading] = useState<boolean>(false);
    const [componentError, setComponentError] = useState<string | null>(null);
    
    /** get value of 'from' key to set breadcrumb path */
    const from = localStorage.getItem('from') || '';
    const path_url = localStorage.getItem('workspace_url') || '';

    const breadcrumbPath = [
        { name: 'Dashboard', path: BaseUrls.getPlatformDashboardUrl() },
        {
          name: from === 'workspace' ? 'Workspaces' : 'Shared RPC APIs',
          path: from === 'workspace' ? BaseUrls.getPlatformWorkspaceUrl() : BaseUrls.getPlatformManageAPIEndpointsUrl(),
        },
    ];

    if (from === 'workspace') {
        breadcrumbPath.push({
          name: localStorage.getItem('workspaceName') as string,
          path: `${BaseUrls.getPlatformWorkspaceUrl()}/${path_url}`,
        });
    }
    //component dependent states
    const [activeTab, setActiveTab] = useState<number>(-1);
    const [activeElement, setActiveElement] = useState<JSX.Element>(<></>);
    const [tabs, setTabs] = useState<Tab[]>([]);
    const [endpointName, setEndpointName] = useState<string>('');

    //refs
    const basicRef = createRef<BIFRef>();

    //modal
    const [showModal, toggleModal] = useState<boolean>(false);
    const [modalTitle, setModalTitle] = useState<string>('');
    const [modalMessage, setModalMessage] = useState<string>('');
    const [modalConfirmText, setModalConfirmText] = useState<string>('OK');
    const [modalConfirmFunc, setModalConfirmFunc] = useState<() => void>();
    const [modalVisibleConfirmBtn, toggleConfirmVisibility] = useState<boolean>(false);
    const [modalVisibleCancelBtn] = useState<boolean>(true);

    useEffect(() => {
        if (!id) return;

        //Populate tabs
        const tabs: Tab[] = [
            {
                element: <Basic endpointId={id} setupEndpointName={setEndpointName} ref={basicRef} />,
                name: 'Basic',
                icon: <img src={basicImage} alt='' />,
            },
            {
                element: <Metrics endpointId={id} />,
                name: 'Metrics',
                icon: <img src={metricsImage} alt='' />,
            },
        ];

        setTabs(tabs);
        setActiveTab(0);
    }, [id]);

    // set the rendering element as an active element
    useEffect(() => {
        const element = tabs.at(activeTab)?.element || <></>;
        setActiveElement(element);
    }, [activeTab]);

    /**
     * To Delete the Endpoint details on backend
     * @returns Promise<void>
     *
     */
    const deleteEndPointDetails = async (): Promise<void> => {
        setComponentError(null);
        try {
            const response: any = await deleteTheEndpoint(id as string);
            if (response && response.success) {
                toggleModal(true);
                setModalTitle('Endpoint');
                setModalMessage('Endpoint deleted successfully');
                toggleConfirmVisibility(false);

                window.open(BaseUrls.getPlatformDashboardUrl(), '_self');
            } else {
                toggleModal(true);
                setModalTitle('Endpoint');
                setModalMessage('Error occured while deleting endpoint');
                toggleConfirmVisibility(false);
            }
            // eslint-disable-next-line
        } catch (error) {
            setComponentError('Failed to delete the endpoint!');
        } finally {
            setLoading(() => false);
        }
    };

    //Event Handlers [start]

    const onClickDelete = () => {
        toggleModal(true);
        setModalTitle('Are you sure you want to delete endpoint');
        toggleConfirmVisibility(true);
        setModalConfirmText('confirm');
        setModalConfirmFunc(() => deleteEndPointDetails);
    };

    //Event Handlers [end]

    return (
        <section className='px-5 col-12'>
            {isLoading && <FullSizeLoader />}
            <div className='mt-5 col-11 mx-auto'>
                <img src={iconShardeum} alt='okx-logo' className='mb-4' width={150} />
                <div className='d-flex justify-content-between align-items-center'>
                    <div className='d-flex align-items-center'>
                        <Breadcrumb
                            prefixLink={breadcrumbPath}
                            activeLink={endpointName ? endpointName : (id as string)}
                            textSize={13}
                        />
                    </div>
                    <div className='d-flex justify-content-end text-end'>
                        <i
                            className='fa-regular fa-trash-can fs-5 cursor'
                            aria-hidden
                            onClick={() => onClickDelete()}
                        />
                    </div>
                </div>
                <div className='col-12 mt-5'>
                    <ul className='nav nav-tabs mt-4' id='myTab' role='tablist'>
                        {tabs.map((tab, index) => {
                            return (
                                <li
                                    key={index}
                                    className='nav-item'
                                    role='presentation'
                                    onClick={() => setActiveTab(index)}
                                >
                                    <button
                                        className={`nav-link px-4 ${activeTab == index ? 'active' : ''}`}
                                        data-bs-toggle='tab'
                                        type='button'
                                        role='tab'
                                    >
                                        {tab.icon}
                                        <span className='p-1'>{tab.name}</span>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                    <div className='tab-content'>
                        <div className='mt-3 px-2'>{activeElement}</div>
                    </div>
                </div>
            </div>
            {componentError && (
                <div className='alert-danger mt-4 rounded text-center'>
                    <p className='text-danger  py-2'>{componentError}</p>
                </div>
            )}
            <Modal
                show={showModal}
                onHide={() => {
                    toggleModal(false);
                    setModalTitle('');
                    setModalMessage('');
                }}
                title={modalTitle}
                visibleFooter
                visibleHeaderCloseIcon={false}
                visibleFooterCloseButton={modalVisibleCancelBtn}
                visibleConfirmBtn={modalVisibleConfirmBtn}
                confirmBtnText={modalConfirmText}
                onConfirm={modalConfirmFunc}
                confirmBtnClassName=''
            >
                <p className='status text-center mt-3'>{modalMessage}</p>
            </Modal>
        </section>
    );
};

export default EndpointDetails;
